import type { FC, ReactNode } from "react";
import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link as RouterLink, useLocation } from "react-router";
import { Box, ListItemButton, Stack, Tooltip, Typography } from "@mui/material";
import type { TranslationKey } from "@privacy/translations";
import { Translate, useTranslation } from "@privacy/translations";
import type { AssistantConversationSummary } from "../../api/assistant";
import {
  assistantConversationQueryOptions,
  deleteAssistantConversationMutationOptions,
  PARTNER_FORM_TYPE,
  REGISTRATION_FORM_TYPE,
  SYSTEM_FORM_TYPE,
} from "../../api/assistant";
import { ActionMenu, ActionMenuDeleteMenuItem, ActionMenuItemWithPermission } from "../../shared/ActionMenu";
import { prefetch } from "../../api/prefetch";
import { ExternalLinkIcon } from "../../shared/icons";
import { RegistrationStatusDot } from "../../shared/RegistrationStatusDot";
import { withQueryParams } from "../../shared/navigateWithQueryParams";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { dayGroup, recordCountKey, recordKinds } from "./assistantConversationMeta";

const OPEN_KEYS: Record<string, TranslationKey> = {
  [REGISTRATION_FORM_TYPE]: "Assistant.Record.Open",
  [SYSTEM_FORM_TYPE]: "Assistant.Record.OpenSystem",
  [PARTNER_FORM_TYPE]: "Assistant.Record.OpenPartner",
};

const MENU_WIDTH = 36;

const AssistantSidebarRow: FC<{ to: string; onHover: () => void; isCurrent?: boolean; children: ReactNode }> = ({
  to,
  onHover,
  isCurrent = false,
  children,
}) => (
  <ListItemButton
    component={RouterLink}
    to={to}
    onMouseEnter={onHover}
    onFocus={onHover}
    selected={isCurrent}
    aria-current={isCurrent ? "page" : undefined}
    sx={{
      "px": 2,
      "py": 1,
      "borderRadius": 1,
      "minHeight": "32px",
      "gap": 1,
      "&:hover": { bgcolor: theme => theme.palette.grey["200"] },
      "&.Mui-selected": { bgcolor: theme => theme.palette.grey["300"] },
      "&:hover.Mui-selected, &.Mui-selected.Mui-focusVisible": { bgcolor: theme => theme.palette.grey["300"] },
      "&.Mui-focusVisible": { outline: "2px solid", outlineColor: theme => theme.palette.primary["300"] },
    }}
  >
    {children}
  </ListItemButton>
);

export const AssistantConversationRow: FC<{ conversation: AssistantConversationSummary; isCurrent: boolean }> = ({
  conversation,
  isCurrent,
}) => {
  const { t, formatDate, language } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const { search } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { mutateAsync: deleteConversation } = useMutation(deleteAssistantConversationMutationOptions());

  const title = conversation.title ?? t("Assistant.History.Untitled");
  const status = conversation.recordStatus;
  const counts = recordKinds(conversation.records).map(kind => t(recordCountKey(kind), { count: String(kind.count) }));
  const meta = [...(status ? [t(`RegistrationStatus.${status}`)] : []), ...counts].join(" · ");
  const made = (conversation.records ?? []).filter(record => !!record.recordId);
  const opened = made.find(record => record.formType === REGISTRATION_FORM_TYPE) ?? made[0];

  const titleRef = useRef<HTMLSpanElement>(null);
  const [isTitleCut, setIsTitleCut] = useState(false);
  const measureTitle = () => {
    const element = titleRef.current;
    setIsTitleCut(!!element && element.scrollWidth > element.clientWidth);
  };

  const open = (formType: string, recordId: string) => {
    if (formType === SYSTEM_FORM_TYPE) return navigate(paths.systemsInline(recordId));
    if (formType === PARTNER_FORM_TYPE) return navigate(paths.partnersInline(recordId));
    return navigate(paths.registrationsInline({ id: recordId, variant: "Controller" }));
  };

  return (
    <Stack
      component="li"
      direction="row"
      sx={{
        "listStyle": "none",
        "alignItems": "center",
        "position": "relative",
        "py": 0.5,
        "&:not(:first-of-type)::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 12,
          right: 12,
          borderTop: "1px solid",
          borderColor: "divider",
        },
        "& .assistant-row-menu": {
          "flexShrink": 0,
          "overflow": "hidden",
          "width": isMenuOpen ? MENU_WIDTH : 0,
          "opacity": isMenuOpen ? 1 : 0,
          "transition": theme =>
            theme.transitions.create(["width", "opacity"], { duration: theme.transitions.duration.shorter }),
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          "& .MuiIconButton-root:focus-visible": { outlineOffset: "-2px" },
        },
        "&:hover .assistant-row-menu, &:focus-within .assistant-row-menu": { width: MENU_WIDTH, opacity: 1 },
        "@media (hover: none)": { "& .assistant-row-menu": { width: MENU_WIDTH, opacity: 1 } },
      }}
    >
      <Tooltip title={isTitleCut ? title : ""} enterDelay={600} placement="right">
        <Box onMouseEnter={measureTitle} onFocus={measureTitle} sx={{ flex: 1, minWidth: 0 }}>
          <AssistantSidebarRow
            isCurrent={isCurrent}
            to={withQueryParams(paths.assistantConversation(conversation.conversationId), search)}
            onHover={() => void prefetch(assistantConversationQueryOptions(conversation.conversationId))}
          >
            <Stack sx={{ minWidth: 0, flex: 1, gap: 0.25 }}>
              <Stack direction="row" sx={{ gap: 1, alignItems: "baseline", minWidth: 0 }}>
                <Typography
                  ref={titleRef}
                  component="span"
                  noWrap
                  sx={{
                    flex: 1,
                    fontSize: theme => theme.typography.pxToRem(14),
                    fontWeight: isCurrent ? 600 : "normal",
                    color: conversation.title ? "text.primary" : "text.secondary",
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  component="time"
                  dateTime={conversation.updatedAt}
                  title={formatDate(conversation.updatedAt, { showTime: true })}
                  sx={{ flexShrink: 0, fontSize: theme => theme.typography.pxToRem(12), color: "text.secondary" }}
                >
                  {shortTime(conversation.updatedAt, language)}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ gap: 0.75, alignItems: "center", minWidth: 0 }}>
                {status && <RegistrationStatusDot status={status} />}
                <Typography
                  component="span"
                  noWrap
                  sx={{ fontSize: theme => theme.typography.pxToRem(12), color: "text.secondary" }}
                >
                  {meta}
                </Typography>
              </Stack>
            </Stack>
          </AssistantSidebarRow>
        </Box>
      </Tooltip>
      <Box className="assistant-row-menu">
        <ActionMenu isOpen={isMenuOpen} onOpenChange={setIsMenuOpen} ariaLabelKey="Assistant.History.RowMenu">
          {opened?.recordId && (
            <ActionMenuItemWithPermission
              onClick={() => {
                setIsMenuOpen(false);
                open(opened.formType, opened.recordId!);
              }}
            >
              <ExternalLinkIcon />
              {t(OPEN_KEYS[opened.formType] ?? "Assistant.Record.Open")}
            </ActionMenuItemWithPermission>
          )}
          <ActionMenuDeleteMenuItem
            onDelete={() => deleteConversation({ conversationId: conversation.conversationId })}
            onDeleteSuccess={() => {
              if (isCurrent) navigate(paths.assistant());
            }}
            onClose={() => setIsMenuOpen(false)}
            confirmDeleteDialog={{
              header: t("Assistant.History.DeleteQuestion"),
              message: (
                <Translate
                  translationKey="Assistant.History.DeleteConfirm"
                  values={{
                    title: (
                      <Box component="strong" sx={{ fontWeight: 600 }}>
                        {title}
                      </Box>
                    ),
                  }}
                />
              ),
            }}
          />
        </ActionMenu>
      </Box>
    </Stack>
  );
};

const shortTime = (isoDate: string, locale: string) => {
  const date = new Date(isoDate);
  return dayGroup(isoDate) === "earlier"
    ? new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(date)
    : new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", hour12: locale === "en" }).format(date);
};
